import axios from 'axios';
import api from './api';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace('localhost', window.location.hostname);

/**
 * Payment Service
 * Handles all payment-related API calls to the backend
 */
export const paymentService = {
    /**
     * Initialize a payment transaction
     * @param {Object} paymentData - Payment details
     * @returns {Promise} Payment initialization response with payment link
     */
    async initializePayment(paymentData) {
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('portalToken');
            
            const response = await axios.post(
                `${API_URL}/api/payments/initialize`,
                paymentData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    timeout: 30000 // 30 second timeout
                }
            );
            return response.data;
        } catch (error) {
            console.error('Payment initialization error:', error);
            const err = new Error(error.response?.data?.detail || 'Failed to initialize payment');
            err.status = error.response?.status;
            throw err;
        }
    },

    /**
     * Get payment status by reference
     * @param {string} reference - Transaction reference
     * @returns {Promise} Transaction status
     */
    async getPaymentStatus(reference) {
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('portalToken');
            const response = await axios.get(
                `${API_URL}/api/payments/status/${reference}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Get payment status error:', error);
            const err = new Error(error.response?.data?.detail || 'Failed to get payment status');
            err.status = error.response?.status;
            throw err;
        }
    },

    /**
     * Get active subscription
     * @returns {Promise} Active subscription details
     */
    async getActiveSubscription() {
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('portalToken');
            const response = await axios.get(
                `${API_URL}/api/payments/subscriptions/active`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) {
                return null; // No active subscription
            }
            console.error('Get active subscription error:', error);
            const err = new Error(error.response?.data?.detail || 'Failed to get subscription');
            err.status = error.response?.status;
            throw err;
        }
    },

    /**
     * Get all user subscriptions
     * @returns {Promise} List of subscriptions
     */
    async getAllSubscriptions() {
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('portalToken');
            const response = await axios.get(
                `${API_URL}/api/payments/subscriptions`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Get subscriptions error:', error);
            const err = new Error(error.response?.data?.detail || 'Failed to get subscriptions');
            err.status = error.response?.status;
            throw err;
        }
    },

    /**
     * Cancel a subscription
     * @param {number} subscriptionId - Subscription ID
     * @returns {Promise} Cancelled subscription
     */
    async cancelSubscription(subscriptionId, immediate = false) {
        try {
            const token = localStorage.getItem('token') || localStorage.getItem('portalToken');
            const response = await axios.post(
                `${API_URL}/api/payments/subscriptions/${subscriptionId}/cancel`,
                { immediate },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Cancel subscription error:', error);
            const err = new Error(error.response?.data?.detail || 'Failed to cancel subscription');
            err.status = error.response?.status;
            throw err;
        }
    },

    /**
     * Detect currency — returns USD as a safe default.
     * Currency selection is now driven by CountryCurrencyContext,
     * which passes the user's selected currency to the backend for
     * server-side conversion via live exchange rates.
     * @returns {string} Currency code
     */
    detectCurrency() {
        return 'USD';
    },

    /**
     * Get currency symbol
     * @param {string} currency - Currency code
     * @returns {string} Currency symbol
     */
    getCurrencySymbol(currency) {
        const symbols = {
            'USD': '$',
            'EUR': '€',
            'GBP': '£',
            'UGX': 'USh'
        };
        return symbols[currency] || currency;
    },

    /**
     * Format amount with currency
     * @param {number} amount - Amount
     * @param {string} currency - Currency code
     * @returns {string} Formatted amount
     */
    formatAmount(amount, currency = 'USD') {
        // Handle undefined or null amounts
        if (amount === undefined || amount === null) {
            return '$0';
        }
        const symbol = this.getCurrencySymbol(currency);
        // Add space for non-dollar currencies
        const space = currency === 'USD' || currency === 'GBP' || currency === 'EUR' ? '' : ' ';
        return `${symbol}${space}${amount.toLocaleString()}`;
    },

    // ── Portal Subscription Management ──────────────────────────────
    // These methods use the shared `api` instance which automatically
    // attaches the portal token and handles token refresh on 401.

    async getPortalSubscription() {
        try {
            const response = await api.get('/api/portal/subscription');
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) return { subscription: null, transactions: [] };
            console.error('Get portal subscription error:', error);
            const err = new Error(error.response?.data?.detail || 'Failed to get subscription');
            err.status = error.response?.status;
            throw err;
        }
    },

    async cancelPortalSubscription(immediate = false) {
        try {
            const response = await api.post('/api/portal/subscription/cancel', { immediate });
            return response.data;
        } catch (error) {
            console.error('Cancel portal subscription error:', error);
            const err = new Error(error.response?.data?.detail || 'Failed to cancel subscription');
            err.status = error.response?.status;
            throw err;
        }
    },

    async reactivatePortalSubscription() {
        try {
            const response = await api.post('/api/portal/subscription/reactivate', {});
            return response.data;
        } catch (error) {
            console.error('Reactivate portal subscription error:', error);
            const err = new Error(error.response?.data?.detail || 'Failed to reactivate subscription');
            err.status = error.response?.status;
            throw err;
        }
    },

    async togglePortalAutoRenew(enabled) {
        try {
            const response = await api.post('/api/portal/subscription/auto-renew', { enabled });
            return response.data;
        } catch (error) {
            console.error('Toggle auto-renew error:', error);
            const err = new Error(error.response?.data?.detail || 'Failed to toggle auto-renew');
            err.status = error.response?.status;
            throw err;
        }
    },

    async renewPortalSubscription(billingPeriod = null, currency = null) {
        try {
            const response = await api.post('/api/portal/subscription/renew', {
                billing_period: billingPeriod,
                currency: currency,
            });
            return response.data;
        } catch (error) {
            console.error('Renew portal subscription error:', error);
            const err = new Error(error.response?.data?.detail || 'Failed to initiate renewal payment');
            err.status = error.response?.status;
            throw err;
        }
    }
};

export default paymentService;
